import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchTraineeRoleData,
  fetchTraineeRoles,
} from '../services/traineeService';
import {
  fetchTraineeRoleAreaData,
  getKnowledgeElementService,
  getTraineeAreaService,
  updateAreaLinkState,
  updateTraineeAreaStatus,
  updateTraineeAreaViewState,
} from '../services/traineeService';
// Async thunks
export const requestTraineeRoleData = createAsyncThunk(
  'trainee/requestTraineeRoleData',
  async (traineeId) => {
    const response = await fetchTraineeRoleData(traineeId);
    return response.data;
  }
);

export const requestTraineeRoles = createAsyncThunk(
  'trainee/requestTraineeRoles',
  async () => {
    const response = await fetchTraineeRoles();
    return response.data;
  }
);

export const updateKnowledgeAreaStatus = createAsyncThunk(
  'trainee/updateKnowledgeAreaStatus',
  async ({ traineeId, areaId, status }) => {
    const response = await updateTraineeAreaStatus(traineeId, areaId, status);
    return response.data;
  }
);

export const updateArea = createAsyncThunk(
  'trainee/updateArea',
  async ({ areaId, roleId }) => {
    const response = await getTraineeAreaService(roleId, areaId);
    return response.data;
  }
);

export const updateKnowledgeElement = createAsyncThunk(
  'trainee/updateKnowledgeElement',
  async ({ personId, elementId }) => {
    const response = await getKnowledgeElementService(personId, elementId);
    return response.data;
  }
);

export const updateTraineeRoleArea = createAsyncThunk(
  'trainee/updateTraineeRoleArea',
  async ({ areaId, roleId }) => {
    const response = await fetchTraineeRoleAreaData(roleId, areaId);
    return response.data;
  }
);

export const setTraineeAreaViewState = createAsyncThunk(
  'trainee/setTraineeAreaViewState',
  async ({ roleId, areaId }, { rejectWithValue }) => {
    try {
      const response = await updateTraineeAreaViewState(roleId, areaId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const changeAreaLinkState = createAsyncThunk(
  'trainee/changeAreaLinkState',
  async ({ roleId, areaId, linkToEmployee }) => {
    const response = await updateAreaLinkState(roleId, areaId, {
      LinkToEmployee: linkToEmployee,
    });
    return {
      ...response.data,
      areaId,
      roleId,
      linkToEmployee,
    };
  }
);

// slice
const traineeSlice = createSlice({
  name: 'trainee',
  initialState: {
    roles: {},
    loading: false,
    error: null,
  },
  reducers: {
    traineeRolesReceived(state, action) {
      state.roles = action.payload;
    },
    traineeRoleDataReceived(state, action) {
      state.selectedRole = action.payload;
    },
    traineeUpdatedAreaDataReceived(state, action) {
      const { areaId, area, completionPercentage, roleId } = action.payload;
      if (state.selectedRole && state.selectedRole.id === roleId) {
        state.selectedRole.knowledgeAreas[areaId] = area;
        state.selectedRole.completionPercentage = completionPercentage;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestTraineeRoles.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestTraineeRoles.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.roles) {
          state.roles = action.payload.roles;
        } else {
          state.roles = action.payload;
        }
        if (action.payload.organisationUnits) {
          state.organisationUnits = action.payload.organisationUnits;
        }
      })
      .addCase(requestTraineeRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(requestTraineeRoleData.pending, (state) => {
        state.fetchingRoleData = true;
      })
      .addCase(requestTraineeRoleData.fulfilled, (state, action) => {
        state.fetchingRoleData = false;
        state.selectedRole = action.payload;
      })
      .addCase(requestTraineeRoleData.rejected, (state, action) => {
        state.fetchingRoleData = false;
        state.error = action.error.message;
      })
      .addCase(updateKnowledgeAreaStatus.pending, (state, action) => {
        state.updatingAreaStatus = true;
      })
      .addCase(updateKnowledgeAreaStatus.fulfilled, (state, action) => {
        const { areaId, status } = action.payload;
        if (state.selectedRole && state.selectedRole.knowledgeAreas[areaId]) {
          state.selectedRole.knowledgeAreas[areaId].status = status;
        }
      })
      .addCase(updateKnowledgeAreaStatus.rejected, (state, action) => {
        state.updatingAreaStatus = false;
        state.error = action.error.message;
      })
      .addCase(updateArea.pending, (state) => {
        state.updatingArea = true;
      })
      .addCase(updateArea.fulfilled, (state, action) => {
        state.updatingArea = false;
        const areaId = action?.meta?.arg?.areaId;
        const roleId = action?.meta?.arg?.roleId;
        const { knowledgeArea, completionPercentage } = action.payload;
        if (state.selectedRole && state.selectedRole.id === roleId) {
          state.selectedRole.knowledgeAreas[areaId] = knowledgeArea;
          state.selectedRole.completionPercentage = completionPercentage;
        }
      })
      .addCase(updateArea.rejected, (state, action) => {
        state.updatingArea = false;
        state.error = action.error.message;
      })
      .addCase(updateKnowledgeElement.pending, (state) => {
        state.updatingElement = true;
      })
      .addCase(updateKnowledgeElement.fulfilled, (state, action) => {
        state.updatingElement = false;
        const elementId = action?.meta?.arg?.elementId;
        const { knowledgeElements } = action.payload;
        const updatedKnowledgeElement = knowledgeElements?.[elementId];
        if (
          state.selectedRole &&
          state.selectedRole.knowledgeElements[elementId]
        ) {
          state.selectedRole.knowledgeElements[elementId] =
            updatedKnowledgeElement;
        }
      })
      .addCase(updateKnowledgeElement.rejected, (state, action) => {
        state.updatingElement = false;
        state.error = action.error.message;
      })
      .addCase(updateTraineeRoleArea.pending, (state) => {
        state.fetchingRoleData = true;
      })
      .addCase(updateTraineeRoleArea.fulfilled, (state, action) => {
        state.fetchingRoleData = false;
        const { knowledgeArea, knowledgeElements } = action.payload;
        if (state.selectedRole) {
          state.selectedRole.knowledgeAreas[knowledgeArea.id] = knowledgeArea;
          state.selectedRole.knowledgeElements = {
            ...state.selectedRole.knowledgeElements,
            ...knowledgeElements,
          };
        }
      })
      .addCase(updateTraineeRoleArea.rejected, (state, action) => {
        state.fetchingRoleData = false;
        state.error = action.error.message;
      })
      .addCase(setTraineeAreaViewState.pending, (state) => {
        state.updatingViewState = true;
      })
      .addCase(setTraineeAreaViewState.fulfilled, (state, action) => {
        state.updatingViewState = false;
        const { roleId, areaId } = action.payload;
        if (state.selectedRole && state.selectedRole.id === roleId) {
          const area = state.selectedRole.knowledgeAreas[areaId];
          if (area) {
            area.viewed = true;
          }
        }
      })
      .addCase(setTraineeAreaViewState.rejected, (state, action) => {
        state.updatingViewState = false;
        state.error = action.error.message;
      })
      .addCase(changeAreaLinkState.pending, (state) => {
        state.updatingLinkState = true;
      })
      .addCase(changeAreaLinkState.fulfilled, (state, action) => {
        state.updatingLinkState = false;
        const { areaId, roleId, completionPercentage, updatedKnowledgeArea } =
          action.payload;
        if (state.selectedRole && state.selectedRole.id === roleId) {
          if (completionPercentage !== undefined) {
            state.selectedRole.completionPercentage = completionPercentage;
          }
          if (updatedKnowledgeArea && updatedKnowledgeArea[areaId]) {
            state.selectedRole.knowledgeAreas[areaId] =
              updatedKnowledgeArea[areaId];
          }
        }
      })
      .addCase(changeAreaLinkState.rejected, (state, action) => {
        state.updatingLinkState = false;
        state.error = action.error.message;
      });
  },
});

export default traineeSlice.reducer;
export const { actions: traineeActions } = traineeSlice;
export const traineeSelector = {
  roles: (state) => state.trainee.roles,
  selectedRole: (state) => state.trainee.selectedRole,
  loading: (state) => state.trainee.loading,
  error: (state) => state.trainee.error,
};
